/**
 * Tutorial overlay — Phase 3 overlay (DOM).
 * Shown when state.scene === "game" and state.tutorialStep >= 0.
 * Replaces drawTutorialOverlay() in canvas-render-adapter.
 */

import type { GameStateView } from "../../application/ports.js";
import { t } from "../../i18n/index.js";

const TUTORIAL_STEP_COUNT = 5;

export interface TutorialOverlayCallbacks {
  onNext?: () => void;
  onSkip?: () => void;
}

export interface TutorialOverlay {
  update(state: GameStateView): void;
  destroy(): void;
}

export function createTutorialOverlay(
  container: HTMLElement,
  cbs: TutorialOverlayCallbacks
): TutorialOverlay {
  const el = document.createElement("div");
  el.className = "hud-tutorial";
  el.style.display = "none";
  container.appendChild(el);

  // Text area
  const textEl = document.createElement("div");
  textEl.className = "hud-tutorial__text";
  el.appendChild(textEl);

  // Step indicator
  const stepEl = document.createElement("div");
  stepEl.className = "hud-tutorial__step";
  el.appendChild(stepEl);

  // Buttons row
  const btnsRow = document.createElement("div");
  btnsRow.className = "hud-tutorial__btns";

  const skipBtn = document.createElement("button");
  skipBtn.className = "hud-tutorial__btn hud-tutorial__btn--skip";
  skipBtn.addEventListener("click", () => cbs.onSkip?.());

  const nextBtn = document.createElement("button");
  nextBtn.className = "hud-tutorial__btn hud-tutorial__btn--next";
  nextBtn.addEventListener("click", () => cbs.onNext?.());

  btnsRow.appendChild(skipBtn);
  btnsRow.appendChild(nextBtn);
  el.appendChild(btnsRow);

  function update(state: GameStateView): void {
    const step = state.tutorialStep ?? -1;
    const visible = state.scene === "game" && step >= 0 && step < TUTORIAL_STEP_COUNT;
    el.style.display = visible ? "" : "none";
    if (!visible) return;

    const stepKey = `tutorial.step${step + 1}`;
    textEl.textContent = t(stepKey);
    stepEl.textContent = `${step + 1} / ${TUTORIAL_STEP_COUNT}`;
    skipBtn.textContent = t("tutorial.skip");
    nextBtn.textContent = t("tutorial.next");
  }

  function destroy(): void {
    el.remove();
  }

  return { update, destroy };
}
